<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-card-container anna-departments">
    <div class="anna-departments-head">
      <oxd-text tag="h6" class="orangehrm-main-title">Departments</oxd-text>
      <span v-if="loaded" class="anna-departments-count">
        {{ departments.length }} department{{
          departments.length === 1 ? '' : 's'
        }}
      </span>
      <oxd-button
        display-type="text"
        :icon-name="isOpen ? 'chevron-up' : 'chevron-down'"
        :label="isOpen ? 'Hide' : 'Edit'"
        :disabled="dirty"
        @click="open = !open"
      />
    </div>
    <oxd-text tag="p" class="anna-departments-intro">
      Each department's own instructions for Anna. A vacancy picks its
      department (Recruitment → Vacancies → the vacancy → Interview), and in
      every interview for that vacancy Anna follows its prompt too: what to look
      for, and questions to ask in "4. The role". An empty prompt adds nothing.
      If you rename a department, pick it again on its vacancies.
    </oxd-text>
    <oxd-text v-if="error" tag="p" class="anna-departments-error">{{
      error
    }}</oxd-text>
    <div v-show="isOpen">
      <oxd-divider />

      <div v-for="(d, i) in departments" :key="d.uid" class="anna-department">
        <div class="anna-department-top">
          <oxd-input-field
            v-model="d.name"
            label="Department"
            placeholder="e.g. Support Specialist"
            class="anna-department-name"
          />
          <oxd-button
            display-type="text"
            icon-name="trash"
            label="Remove"
            @click="remove(i)"
          />
        </div>
        <textarea
          v-model="d.prompt"
          class="anna-department-prompt"
          :aria-label="`Prompt for ${d.name || 'this department'}`"
          placeholder="Empty: Anna adds nothing for this department. For example: what matters most in this role, and questions to ask."
          spellcheck="true"
        ></textarea>
      </div>
      <oxd-text v-if="loaded && !departments.length" tag="p" class="anna-meta">
        No departments yet.
      </oxd-text>

      <div :class="['anna-departments-actions', {'anna-save-float': dirty}]">
        <oxd-button
          display-type="ghost"
          icon-name="plus"
          label="Add department"
          @click="add"
        />
        <span class="anna-departments-spacer"></span>
        <oxd-text v-if="dirty" tag="p" class="anna-departments-unsaved"
          >Not saved yet</oxd-text
        >
        <oxd-button
          v-if="dirty"
          display-type="ghost"
          label="Undo my changes"
          :disabled="busy"
          @click="undo"
        />
        <oxd-button
          display-type="secondary"
          label="Save departments"
          :disabled="busy || !dirty"
          @click="save"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {guardLeaving} from '../util/leaveGuard';

let uid = 0;
const editable = (list) =>
  (list || []).map((d) => ({uid: ++uid, name: d.name, prompt: d.prompt || ''}));
const plain = (list) =>
  JSON.stringify(
    list.map((d) => ({name: d.name.trim(), prompt: d.prompt.trim()})),
  );

// HR's department prompts, kept on ANNA (ANNA Settings → Anna): add, rename, remove, save the list.
export default {
  name: 'DepartmentPrompts',
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/anna/departments',
    );
    return {http};
  },
  data() {
    return {
      departments: [],
      saved: '[]',
      // folded to its title until "Edit"; unsaved changes keep it open
      open: false,
      loaded: false,
      busy: false,
      error: null,
    };
  },
  computed: {
    isOpen() {
      return this.open || this.dirty;
    },
    dirty() {
      return this.loaded && plain(this.departments) !== this.saved;
    },
  },
  created() {
    this.load();
  },
  mounted() {
    this.unguard = guardLeaving(() => this.dirty);
  },
  beforeUnmount() {
    this.unguard();
  },
  methods: {
    apply(list) {
      this.departments = editable(list);
      this.saved = plain(this.departments);
      this.loaded = true;
    },
    load() {
      this.http
        .request({method: 'GET'})
        .then(({data: {data}}) => this.apply(data))
        .catch(() => {
          this.error =
            "The departments couldn't be loaded right now (ANNA unavailable?).";
        });
    },
    add() {
      this.departments.push({uid: ++uid, name: '', prompt: ''});
    },
    remove(i) {
      const d = this.departments[i];
      if (
        d.prompt.trim() &&
        !window.confirm(
          `Remove "${d.name || 'this department'}" and its prompt?`,
        )
      )
        return;
      this.departments.splice(i, 1);
    },
    undo() {
      this.apply(JSON.parse(this.saved));
    },
    save() {
      this.busy = true;
      this.http
        .request({
          method: 'PUT',
          data: {
            departments: this.departments.map((d) => ({
              name: d.name,
              prompt: d.prompt,
            })),
          },
        })
        .then(({data: {data}}) => {
          this.apply(data);
          return this.$toast.saveSuccess();
        })
        // ANNA's reason ("Two departments are called …") comes as the API's toast
        .catch(() => null)
        .finally(() => {
          this.busy = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.anna-departments-head {
  display: flex;
  align-items: center;
  gap: 12px;
  .orangehrm-main-title {
    flex-grow: 1;
  }
}
.anna-departments-count {
  font-size: 12px;
  color: #6a7482;
}
.anna-departments-intro {
  font-size: 13px;
  line-height: 1.6;
  color: #3d4757;
  margin-top: 4px;
}
.anna-departments-error {
  color: #a05a00;
  font-size: 13px;
}
.anna-meta {
  font-size: 12px;
  color: #64728c;
}
.anna-department {
  margin-bottom: 16px;
}
.anna-department-top {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}
.anna-department-name {
  flex: 1;
  max-width: 420px;
}
.anna-department-prompt {
  width: 100%;
  box-sizing: border-box;
  min-height: 90px;
  field-sizing: content;
  max-height: 420px;
  margin-top: 6px;
  resize: vertical;
  padding: 10px 12px;
  border: 1px solid #dde3ea;
  border-radius: 10px;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.6;
  color: #16202b;
  background: #ffffff;
}
.anna-departments-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.anna-departments-spacer {
  flex: 1;
}
.anna-departments-unsaved {
  font-size: 12px;
  font-weight: 700;
  color: #a05a00;
}
</style>
